/**
 * API-backed Leagues store.
 *
 * Mirrors the prototype store's external-store contract (`subscribeToLeagues` /
 * `getLeaguesSnapshot` returning a `LeaguesState`) so the same React hooks can
 * read from either source. Instead of a client-side simulation timer, this store
 * loads from the backend and polls while there are active subscribers — real
 * settlements land via the lifecycle/settlement cron and show up on the next poll.
 *
 * Actions call the real endpoints in `leagueApi.ts` and merge the fresh league
 * the server returns back into the cached snapshot, then notify subscribers.
 */

import {
    apiCreateLeague,
    apiDeclineLeague,
    apiEnterBullionBattle,
    apiGetBullionBattle,
    apiGetLeague,
    apiGetLeagueByCode,
    apiGetVipStatus,
    apiInviteFriends,
    apiJoinLeague,
    apiLeaguesFeed,
    apiPlaceLeaguePlay,
    apiTopUpLeagueSeat,
    LeagueApiError,
    type PlaceLeaguePlayInput,
} from "./leagueApi";
import { isPersistedLeagueId } from "./persistedLeagueId";
import {
    finalLeaguesFingerprint,
    isUnchangedLeagueStub,
    leagueFinalMarker,
    mergeListSlimLeague,
    mergePolledLeagueDetail,
} from "./leagueListFinal";
import {
    completeWireLeague,
    hydrateWireLeague,
    leagueWireHave,
    resetLeagueWire,
    type WireLeague,
} from "./leagueWire";
import type {
    CreateLeagueInput,
    League,
    LeagueBullionBattle,
    LeagueBullionBattleViewerSlip,
    LeaguesState,
} from "./types";
import {
    applyLeagueReactionToMembers,
    leagueReactionActivityFromThrow,
    prependLeagueReactionEvent,
} from "./leagueReactionStats";
import type { LeagueReactionEmoji } from "./leagueReactions";

const DEFAULT_POLL_MS = 15000;
/** No live (or about to start) league in the list: hub cards and the nav badge barely change. */
const IDLE_POLL_MS = 60000;
/** Treat a league as active this long before kickoff so the first-ball switch is picked up. */
const ACTIVE_BEFORE_START_MS = 30 * 60_000;
/**
 * Open live league page: full feed (plays / settlements / activity). Hub list is card-only.
 * Matches the ~5s server feed cache so every member of a live league sees new rows quickly
 * without each client bypassing the shared snapshot. Prematch / ended pages hydrate once.
 */
const DEFAULT_DETAIL_POLL_MS = 4000;

function pollIntervalMs(): number {
    const raw = Number(process.env.LEAGUE_API_POLL_MS);
    return Number.isFinite(raw) && raw >= 3000 ? raw : DEFAULT_POLL_MS;
}

export function leagueDetailPollMs(): number {
    const raw = Number(process.env.LEAGUE_DETAIL_POLL_MS);
    return Number.isFinite(raw) && raw >= 2000 ? raw : DEFAULT_DETAIL_POLL_MS;
}

/** Real data has no client-side simulation; `simulating` stays false. */
const EMPTY_STATE: LeaguesState = { leagues: [], vip: false, simulating: false };

/** `string` = logged-in user id, `null` = guest, `undefined` = not reported yet by the UI. */
type ViewerKey = string | null | undefined;

/** v2 stores leagues in wire form where possible, so the first poll after a reload is a delta. */
const CACHE_KEY_PREFIX = "c8_leagues_cache_v2_";
const LEGACY_CACHE_KEY_PREFIX = "c8_leagues_cache_v1_";
/** Skip persisting very large payloads (plays/settlements) rather than blowing the storage quota. */
const CACHE_MAX_CHARS = 1_500_000;

const listeners = new Set<() => void>();
let state: LeaguesState = EMPTY_STATE;
let pollId: ReturnType<typeof setTimeout> | null = null;
let loadedOnce = false;
let inFlight: Promise<void> | null = null;
let inFlightKey: ViewerKey = undefined;
let viewerKey: ViewerKey = undefined;
/**
 * Leagues trusted for the final-league fingerprint: server-fresh in this session, or finalized
 * rows hydrated from localStorage (settledAt/cancelledAt are immutable, so a matching
 * fingerprint can stub them on the first poll without a full past-league download).
 */
const serverFreshIds = new Set<string>();

function knownFinalFingerprint(): string | null {
    const markers: string[] = [];
    for (const league of state.leagues) {
        if (isSharePreviewLeague(league) || !serverFreshIds.has(league.id)) continue;
        const marker = leagueFinalMarker(league);
        if (marker) markers.push(marker);
    }
    return markers.length > 0 ? finalLeaguesFingerprint(markers) : null;
}

/** Trust cached finals for the fingerprint so cold loads can stub past leagues immediately. */
function markCachedFinalsFresh(leagues: League[]): void {
    for (const league of leagues) {
        if (isSharePreviewLeague(league)) continue;
        if (leagueFinalMarker(league)) serverFreshIds.add(league.id);
    }
}

function readCache(key: string): { leagues: League[]; vip: boolean } | null {
    try {
        const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${key}`);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { leagues?: unknown; vip?: unknown };
        if (!Array.isArray(parsed.leagues)) return null;
        const leagues = (parsed.leagues as Array<League | WireLeague>)
            .map((row) => hydrateWireLeague(row))
            .filter((row): row is League => row != null);
        return { leagues, vip: Boolean(parsed.vip) };
    } catch {
        return null;
    }
}

function writeCache(key: string, leagues: League[], vip: boolean): void {
    try {
        localStorage.removeItem(`${LEGACY_CACHE_KEY_PREFIX}${key}`);
        const stored = leagues.map((league) => completeWireLeague(league) ?? league);
        const raw = JSON.stringify({ leagues: stored, vip });
        if (raw.length > CACHE_MAX_CHARS) {
            localStorage.removeItem(`${CACHE_KEY_PREFIX}${key}`);
            return;
        }
        localStorage.setItem(`${CACHE_KEY_PREFIX}${key}`, raw);
    } catch {
        /* Storage full / disabled — the cache is only a speed-up. */
    }
}

function emit(): void {
    listeners.forEach((listener) => listener());
}

/** Only replace state (and re-render) when something actually changed. */
function commit(next: LeaguesState): void {
    if (sameState(state, next)) return;
    state = next;
    if (next.loadedFor && next.loadedFor === viewerKey) {
        writeCache(next.loadedFor, next.leagues.filter((row) => !isSharePreviewLeague(row)), next.vip);
    }
    emit();
}

function sameState(a: LeaguesState, b: LeaguesState): boolean {
    if (a === b) return true;
    if (a.vip !== b.vip || a.simulating !== b.simulating) return false;
    if (a.loadedFor !== b.loadedFor || a.hydratedFor !== b.hydratedFor) return false;
    if (a.leagues.length !== b.leagues.length) return false;
    // Hydration hands back the same object for an unchanged league, so only rebuilt ones are
    // compared structurally (they can still be equal, e.g. a mutation response).
    return a.leagues.every(
        (league, i) => league === b.leagues[i] || JSON.stringify(league) === JSON.stringify(b.leagues[i]),
    );
}

function upsertLeague(league: League | null | undefined): void {
    if (!league) return;
    serverFreshIds.add(league.id);
    const idx = state.leagues.findIndex((row) => row.id === league.id);
    // A league the viewer declined/left should drop out of their list on refresh;
    // upsert only ever adds or replaces — removal happens via a full refresh.
    const leagues =
        idx >= 0
            ? state.leagues.map((row, i) => (i === idx ? league : row))
            : [league, ...state.leagues];
    commit({ ...state, leagues });
}

const appliedReactionIds = new Set<string>();

/** Optimistic sent/received tallies so the board updates before the next feed poll. */
export function recordLiveLeagueReaction(input: {
    reactionId: string;
    leagueId: string;
    fromUserId: number;
    fromPlayerName?: string;
    targetUserId: number;
    emoji: LeagueReactionEmoji;
}): void {
    const { reactionId, leagueId, fromUserId, targetUserId, emoji } = input;
    if (!reactionId || !leagueId || fromUserId === targetUserId) return;
    if (appliedReactionIds.has(reactionId)) return;
    appliedReactionIds.add(reactionId);
    if (appliedReactionIds.size > 80) {
        const keep = [...appliedReactionIds].slice(-40);
        appliedReactionIds.clear();
        for (const id of keep) appliedReactionIds.add(id);
    }
    const current = findLeagueApi(leagueId);
    if (!current) return;
    const event = leagueReactionActivityFromThrow({
        reactionId,
        members: current.members,
        fromUserId,
        fromPlayerName: input.fromPlayerName,
        targetUserId,
        emoji,
    });
    upsertLeague({
        ...current,
        members: applyLeagueReactionToMembers(current.members, { fromUserId, targetUserId, emoji }),
        reactionEvents: event
            ? prependLeagueReactionEvent(current.reactionEvents, event)
            : current.reactionEvents,
    });
}

/** Activity-feed only — does not bump sent/received so the leaderboard hit can play first. */
export function recordLiveLeagueReactionActivity(input: {
    reactionId: string;
    leagueId: string;
    fromUserId: number;
    fromPlayerName?: string;
    targetUserId: number;
    emoji: LeagueReactionEmoji;
}): void {
    const { reactionId, leagueId, fromUserId, targetUserId, emoji } = input;
    if (!reactionId || !leagueId || fromUserId === targetUserId) return;
    const current = findLeagueApi(leagueId);
    if (!current) return;
    const event = leagueReactionActivityFromThrow({
        reactionId,
        members: current.members,
        fromUserId,
        fromPlayerName: input.fromPlayerName,
        targetUserId,
        emoji,
    });
    if (!event) return;
    if ((current.reactionEvents ?? []).some((row) => row.id === event.id)) return;
    upsertLeague({
        ...current,
        reactionEvents: prependLeagueReactionEvent(current.reactionEvents, event),
    });
}

/** Reflect a player-name change in every held league now, instead of waiting for the next poll. */
export function renameLeagueMember(userId: number, playerName: string): void {
    let changed = false;
    const leagues = state.leagues.map((league) => {
        if (!league.members.some((m) => m.userId === userId && m.playerName !== playerName)) return league;
        changed = true;
        return {
            ...league,
            members: league.members.map((m) => (m.userId === userId ? { ...m, playerName } : m)),
        };
    });
    if (changed) commit({ ...state, leagues });
}

function removeLeague(leagueId: string): void {
    const leagues = state.leagues.filter((row) => row.id !== leagueId);
    if (leagues.length === state.leagues.length) return;
    commit({ ...state, leagues });
}

/**
 * Share-link / join-landing leagues the viewer has not joined yet.
 * `GET /leagues` never returns these, so a full list refresh must not drop them —
 * especially when logged out (list 401) while Header still polls the store.
 */
function isSharePreviewLeague(league: League): boolean {
    return league.membership === "none";
}

/** Keep join-by-code (and similar) previews that the viewer's list endpoint omits. */
function mergeListedWithPreviews(listed: League[]): League[] {
    const listedIds = new Set(listed.map((row) => row.id));
    const previews = state.leagues.filter(
        (row) => isSharePreviewLeague(row) && !listedIds.has(row.id),
    );
    return previews.length === 0 ? listed : [...listed, ...previews];
}

export function getLeaguesApiSnapshot(): LeaguesState {
    return state;
}

/**
 * Tell the store who is viewing (user id, or null for guests). On a change it swaps in that
 * viewer's persisted list so the hub / nav render instantly, then revalidates from the server.
 */
export function setLeaguesViewer(key: string | null): void {
    if (key === viewerKey) return;
    viewerKey = key;
    if (state.loadedFor === key) return;
    serverFreshIds.clear();
    resetLeagueWire();
    const cached = key ? readCache(key) : null;
    if (cached?.leagues?.length) markCachedFinalsFresh(cached.leagues);
    commit({
        leagues: mergeListedWithPreviews(cached?.leagues ?? []),
        vip: cached?.vip ?? false,
        simulating: false,
        hydratedFor: cached ? key : undefined,
    });
    void refreshLeagues();
}

/** VIP rarely changes: background polls reuse it for this long; explicit refreshes always recheck. */
const VIP_POLL_REFRESH_MS = 5 * 60_000;
let vipCheckedAt = 0;
let vipCheckedFor: ViewerKey | undefined;
let lastRefreshAt = 0;

function isTabHidden(): boolean {
    return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/** Refresh the whole list + VIP flag from the server. */
export async function refreshLeagues(opts: { poll?: boolean } = {}): Promise<void> {
    if (inFlight) {
        // A request started before the viewer was known is adopted by whoever reports first.
        if (inFlightKey === undefined || inFlightKey === viewerKey) return inFlight;
        return inFlight.then(() => refreshLeagues());
    }
    const requestedKey = viewerKey;
    inFlightKey = requestedKey;
    const resolveKey = (): ViewerKey => (requestedKey === undefined ? viewerKey : requestedKey);
    lastRefreshAt = Date.now();
    const vipFresh =
        opts.poll === true && vipCheckedFor === requestedKey && Date.now() - vipCheckedAt < VIP_POLL_REFRESH_MS;
    inFlight = (async () => {
        try {
            const final = knownFinalFingerprint();
            const [firstListed, vip] = await Promise.all([
                apiLeaguesFeed(final, leagueWireHave()),
                vipFresh
                    ? state.vip
                    : apiGetVipStatus().then(
                          (v) => {
                              vipCheckedAt = Date.now();
                              vipCheckedFor = requestedKey;
                              return v;
                          },
                          () => state.vip,
                      ),
            ]);
            let listed = firstListed;
            let hydrated = listed.map((row) => (isUnchangedLeagueStub(row) ? row : hydrateWireLeague(row)));
            if (hydrated.includes(null)) {
                // A reused chunk was dropped by a racing fetch — ask again without reuse.
                resetLeagueWire();
                listed = await apiLeaguesFeed(final, {});
                hydrated = listed.map((row) => (isUnchangedLeagueStub(row) ? row : hydrateWireLeague(row)));
            }
            const key = resolveKey();
            // Viewer switched mid-flight — this payload belongs to someone else.
            if (key !== viewerKey) return;
            loadedOnce = true;
            const heldById = new Map(state.leagues.map((row) => [row.id, row]));
            const leagues: League[] = [];
            let missingHeld = false;
            for (const row of hydrated) {
                if (!row) continue;
                if (isUnchangedLeagueStub(row)) {
                    const held = heldById.get(row.id);
                    if (held) {
                        serverFreshIds.add(row.id);
                        leagues.push(held);
                    } else missingHeld = true;
                    continue;
                }
                serverFreshIds.add(row.id);
                leagues.push(mergeListSlimLeague(heldById.get(row.id), row));
            }
            const listedIds = new Set(listed.map((row) => row.id));
            for (const id of Object.keys(leagueWireHave())) {
                if (!listedIds.has(id)) resetLeagueWire(id);
            }
            // A stub we can't resolve (e.g. removed mid-flight): force a full list next poll.
            if (missingHeld) serverFreshIds.clear();
            commit({
                leagues: mergeListedWithPreviews(leagues),
                vip,
                simulating: false,
                loadedFor: key,
                hydratedFor: key,
            });
        } catch (e) {
            // Keep the last good snapshot on a transient failure; polling will retry.
            // On 401 (logged out), drop viewer leagues but keep share-code previews so the
            // join landing is not wiped by Header's store subscription.
            if (e instanceof LeagueApiError && e.status === 401) {
                const key = resolveKey();
                if (key !== viewerKey) return;
                loadedOnce = true;
                serverFreshIds.clear();
                resetLeagueWire();
                vipCheckedFor = undefined;
                const previews = state.leagues.filter(isSharePreviewLeague);
                commit({ leagues: previews, vip: false, simulating: false, loadedFor: key, hydratedFor: key });
            }
        } finally {
            inFlight = null;
            inFlightKey = undefined;
        }
    })();
    return inFlight;
}

function hasActiveLeague(now: number): boolean {
    return state.leagues.some((league) => {
        if (isSharePreviewLeague(league) || league.settledAt || league.cancelledAt) return false;
        const start = new Date(league.startsAt).getTime();
        const end = new Date(league.endsAt).getTime();
        if (Number.isFinite(end) && now >= end) return false;
        return !Number.isFinite(start) || now >= start - ACTIVE_BEFORE_START_MS;
    });
}

/** Fast while a league is live or about to start; otherwise an idle cadence. */
function currentPollMs(): number {
    const fast = pollIntervalMs();
    return hasActiveLeague(Date.now()) ? fast : Math.max(fast, IDLE_POLL_MS);
}

/** Background tabs skip ticks; coming back catches up at once if a tick was missed. */
function onVisibilityChange(): void {
    if (viewerKey === null) return;
    if (!isTabHidden() && Date.now() - lastRefreshAt >= currentPollMs()) void refreshLeagues({ poll: true });
}

function scheduleNextPoll(): void {
    pollId = setTimeout(() => {
        // Logged-out viewers have no league list to poll (the feed would 401 every tick).
        if (!isTabHidden() && viewerKey !== null && Date.now() - lastRefreshAt >= currentPollMs() - 1000) {
            void refreshLeagues({ poll: true });
        }
        scheduleNextPoll();
    }, pollIntervalMs());
}

function startPolling(): void {
    if (typeof window === "undefined" || pollId != null) return;
    scheduleNextPoll();
    document.addEventListener("visibilitychange", onVisibilityChange);
}

function stopPolling(): void {
    if (pollId != null) {
        clearTimeout(pollId);
        pollId = null;
        document.removeEventListener("visibilitychange", onVisibilityChange);
    }
}

export function subscribeToLeaguesApi(listener: () => void): () => void {
    listeners.add(listener);
    if (listeners.size === 1) {
        if (!loadedOnce) void refreshLeagues();
        startPolling();
    }
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stopPolling();
    };
}

const detailInFlight = new Map<string, Promise<League | null>>();

/** Fetch one league and merge it in — used by the detail route (and its live poll). */
export async function loadLeagueDetail(
    leagueId: string,
    opts?: { fresh?: boolean },
): Promise<League | null> {
    const key = `${leagueId}:${opts?.fresh ? "1" : "0"}`;
    const existing = detailInFlight.get(key);
    if (existing) return existing;
    const promise = (async () => {
        try {
            const league = await apiGetLeague(leagueId, { fresh: Boolean(opts?.fresh) });
            if (!league) return findLeagueApi(leagueId);
            upsertLeague(mergePolledLeagueDetail(findLeagueApi(leagueId), league));
            return findLeagueApi(leagueId) ?? league;
        } catch {
            return findLeagueApi(leagueId);
        } finally {
            detailInFlight.delete(key);
        }
    })();
    detailInFlight.set(key, promise);
    return promise;
}

/** Fetch a league by share code fresh and merge it in — used by the join landing. */
export async function loadLeagueByCode(code: string): Promise<League | null> {
    try {
        const league = await apiGetLeagueByCode(code);
        upsertLeague(league);
        return league;
    } catch (e) {
        if (e instanceof LeagueApiError && e.status === 429) throw e;
        const held = findLeagueByCodeApi(code);
        if (held) return held;
        // Finished leagues the viewer already joined still appear on GET /leagues; by-code can
        // 404/500 while the list is cold or the join endpoint is flaky — refresh once and retry.
        try {
            await refreshLeagues();
        } catch {
            /* keep going — find below may still hit a cached row */
        }
        return findLeagueByCodeApi(code);
    }
}

export function findLeagueApi(leagueId: string | undefined): League | null {
    if (!leagueId) return null;
    return state.leagues.find((row) => row.id === leagueId) ?? null;
}

/** Optimistic/local update after POST/DELETE /leagues/:id/remind. */
export function patchLeagueRemindMeApi(leagueId: string, remindMe: boolean): void {
    const current = findLeagueApi(leagueId);
    if (!current) return;
    upsertLeague({ ...current, remindMe: Boolean(remindMe) });
}

export function findLeagueByCodeApi(code: string | undefined): League | null {
    const needle = String(code ?? "").trim().toUpperCase();
    if (!needle) return null;
    return state.leagues.find((row) => row.code.toUpperCase() === needle) ?? null;
}

export async function createLeagueApi(input: CreateLeagueInput): Promise<League> {
    const league = await apiCreateLeague(input);
    upsertLeague(league);
    return league;
}

export async function joinLeagueApi(target: {
    leagueId?: string;
    code?: string;
    inviterPlayerId?: string;
}): Promise<League> {
    const league = await apiJoinLeague(target);
    upsertLeague(league);
    return league;
}

export async function topUpLeagueSeatApi(leagueId: string): Promise<League> {
    const league = await apiTopUpLeagueSeat(leagueId);
    upsertLeague(league);
    return league;
}

export async function declineLeagueApi(leagueId: string): Promise<void> {
    await apiDeclineLeague(leagueId);
    removeLeague(leagueId);
}

export async function inviteFriendsApi(leagueId: string, userIds: number[]): Promise<number> {
    const { invited, league } = await apiInviteFriends(leagueId, userIds);
    upsertLeague(league);
    return invited;
}

export async function placeLeaguePlayApi(
    leagueId: string,
    input: PlaceLeaguePlayInput,
): Promise<string> {
    const { playId, league } = await apiPlaceLeaguePlay(leagueId, input);
    upsertLeague(league);
    return playId;
}

/** Merge a freshly-fetched Bullion battle onto the cached league (keeps other league fields). */
function mergeBattleIntoLeague(leagueId: string, battle: LeagueBullionBattle | null): void {
    const current = findLeagueApi(leagueId);
    if (!current) return;
    // A not-ready / empty battle response must not wipe a slip the lobby already has.
    if (!battle) return;
    upsertLeague({ ...current, bullionBattle: battle });
}

/**
 * Fetch (and lazily create, server-side) the league's Bullion battle, merging it onto the cached
 * league so the lobby card renders real data. Returns the battle (or null when not ready yet).
 */
export async function loadBullionBattleApi(
    leagueId: string,
): Promise<{ battle: LeagueBullionBattle | null; reason: string | null }> {
    if (!isPersistedLeagueId(leagueId)) {
        return { battle: null, reason: null };
    }
    try {
        const { battle, reason } = await apiGetBullionBattle(leagueId);
        mergeBattleIntoLeague(leagueId, battle);
        return { battle, reason };
    } catch {
        return { battle: findLeagueApi(leagueId)?.bullionBattle ?? null, reason: null };
    }
}

/** Enter (or replace) the viewer's Bullion battle slip; merges the fresh league the server returns. */
export async function enterBullionBattleApi(
    leagueId: string,
    slip: LeagueBullionBattleViewerSlip,
): Promise<{ playId: string; battle: LeagueBullionBattle | null }> {
    const { playId, league, battle } = await apiEnterBullionBattle(leagueId, slip);
    if (league) upsertLeague(league);
    else mergeBattleIntoLeague(leagueId, battle);
    return { playId, battle };
}
